// src/views/home.ts
import { html, type TemplateResult, nothing } from 'lit-html';
import { DIMS, DIM_NAME, DIM_NAME_EN, DIM_EMOJI, DIM_HOME_TAGLINE } from '../constants/dimensions.js';
import { formatDate } from '../modules/format.js';
import type { Store } from '../modules/store.js';

interface RenderHomeParams {
  store?: Store;
}

export function renderHome({ store }: RenderHomeParams = {}): TemplateResult {
  const last = store?.history[0] ?? null;
  return html`
    <div class="container home">
      <header class="hero">
        <h1>VARK 学习风格测试</h1>
        <p class="subtitle">发现你的学习偏好 — 科学严谨,温暖呈现</p>
        <ul class="hero-meta">
          <li>📝 16 道题</li>
          <li>⏱ 约 5 分钟</li>
          <li>🆓 完全免费</li>
          <li>🔒 数据仅存浏览器</li>
        </ul>
      </header>

      <section class="hero-actions">
        <a href="#/intro" class="btn btn-primary">开始测试 →</a>
        ${last ? html`
          <a href="#/result" class="btn btn-secondary">
            查看最近结果(${formatDate(last.completedAt)} · ${last.result.code})
          </a>
        ` : nothing}
      </section>

      <section class="home-section">
        <h2>VARK 四个维度</h2>
        <p class="section-lede">每个人吸收信息的偏好通道不同。VARK 模型把它分为四类,你的画像就是这四类的组合。</p>
        <div class="dim-grid">
          ${DIMS.map(d => html`
            <div class="dim-tile dim-tile-${d}">
              <div class="dim-tile-emoji">${DIM_EMOJI[d]}</div>
              <h3>${d} · ${DIM_NAME_EN[d]} ${DIM_NAME[d]}</h3>
              <p>${DIM_HOME_TAGLINE[d]}</p>
            </div>
          `)}
        </div>
      </section>

      <section class="home-section">
        <h2>三步完成测试</h2>
        <ol class="steps">
          <li>
            <span class="step-num">1</span>
            <div>
              <h3>选择题库 · 完成 16 题</h3>
              <p>VARK 官方原版(中译双语)或自研中文版,二选一,每题至少选 1 项。</p>
            </div>
          </li>
          <li>
            <span class="step-num">2</span>
            <div>
              <h3>系统按官方算法计分</h3>
              <p>严格遵循 Fleming 官方阶梯计分法 (stepping rule):总分查表得 step,与最高分差不超过 step 的维度都纳入偏好。</p>
            </div>
          </li>
          <li>
            <span class="step-num">3</span>
            <div>
              <h3>看到你的画像</h3>
              <p>四维分数 + 雷达图 + 维度释义 + 个性化学习策略 + 学科职业方向参考。</p>
            </div>
          </li>
        </ol>
      </section>

      <section class="home-section">
        <h2>关于科学性</h2>
        <div class="science-card">
          <p>本测试遵循由 <strong>Neil D. Fleming</strong>(新西兰林肯大学,1987)提出的 VARK 模型,完整保留官方 16 题问卷与 stepping rule 计分。</p>
          <p>Leite, Svinicki &amp; Shi (2010) 的多特征-多方法验证研究显示 VARK 各维度内部一致性 α ≈ 0.77 ~ 0.85,符合心理量表可接受范围。</p>
          <p class="science-note">⚠ 学习风格理论本身仍存在学术争议(Pashler et al. 2008)。本测试结果只描述 <em>偏好倾向</em>,不应用于"必须按某种方式学才有效"的强制结论。</p>
          <p><a href="#/about" class="science-link">查看完整方法学与文献引用 →</a></p>
        </div>
      </section>

      <section class="home-section">
        <h2>常见问题</h2>
        <details class="faq">
          <summary>测试结果会保存在哪里?</summary>
          <p>仅保存在你当前浏览器的 localStorage 中,我们的服务器看不到也不存任何数据。清空浏览器缓存数据会被一并清除。</p>
        </details>
        <details class="faq">
          <summary>需要注册账号吗?</summary>
          <p>不需要。完全匿名,无需邮箱、无需手机号、无需登录。</p>
        </details>
        <details class="faq">
          <summary>测试结果可以多次重做吗?</summary>
          <p>可以。每次完成后旧结果会进入"历史"(最多保留 5 条最近的),首页"查看最近结果"会显示最新一次。</p>
        </details>
        <details class="faq">
          <summary>题库 A 与题库 B 有什么区别?</summary>
          <p>A 是 VARK 官方 v8 原版 16 题(中译双语,© Neil Fleming),最权威;B 是基于 VARK 模型自研的中文等价题库,情景全为本土化场景。两套都使用相同的官方计分算法,结果可以横向对比。</p>
        </details>
      </section>

      <section class="hero-secondary">
        <a href="#/about">了解 VARK 模型</a>
        ·
        <a href="#/privacy">隐私说明</a>
      </section>
    </div>
  `;
}
